import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PaginationBar from '../../components/PaginationBar';
import { ProductVariant, fetchProductVariants } from '../../api/productVariants';
import { runSyncJob } from '../../api/syncData';

interface VariantsPanelProps {
  onSelect?: (id: string) => void;
  servicesOnly?: boolean;
}

interface VariantsState {
  records: ProductVariant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const VariantsPanel: FC<VariantsPanelProps> = ({ onSelect, servicesOnly = false }) => {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<VariantsState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Removed vehicle brand/model filters for this view

  const listLabel = servicesOnly ? 'service variants' : 'variants';
  const searchPlaceholder = servicesOnly ? 'Search service variants...' : 'Search variants...';
  const loadingMessage = servicesOnly ? 'Loading service variants...' : 'Loading variants...';
  const emptyMessage = servicesOnly ? 'No service variants found.' : 'No variants found.';
  const errorMessage = servicesOnly
    ? 'Failed to load service variants.'
    : 'Failed to load product variants.';

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const isActive =
      statusFilter === 'all' ? undefined : statusFilter === 'active';

    fetchProductVariants({
      page,
      limit,
      search: debouncedSearch || undefined,
      createdByWorkshopManagement: servicesOnly ? true : false,
      isActive,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setState({
          records: response.data,
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : errorMessage);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch, servicesOnly, refreshCounter, errorMessage, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleSync = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    try {
      await runSyncJob('productVariants');
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to sync ${listLabel}.`;
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-40"
          >
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {statusFilter !== 'all' && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
                setStatusFilter('all');
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>

        {!servicesOnly ? (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from ERP'}
          </button>
        ) : null}
      </div>

      {/* Vehicle filters removed for variants listing */}

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">ERP ID</th>
              <th className="px-5 py-3 font-semibold">UPC</th>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Brand</th>
              <th className="px-5 py-3 font-semibold">Retail Price</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Source</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={9}>
                  {loadingMessage}
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={9}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={9}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              state.records.map((variant) => (
                <tr key={variant.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{variant.erpId}</td>
                  <td className="px-5 py-3 text-slate-500">{variant.upc ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{variant.name}</td>
                  <td className="px-5 py-3 text-slate-500">{variant.brand?.name ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {variant.retailPrice ? `${variant.retailPrice} ${variant.currencyCode ?? ''}` : '—'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {variant.isActive ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {variant.isCreatedByWorkshopManagement ? 'Workshop' : 'ERP'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {new Date(variant.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-primary transition hover:bg-primary/10"
                        onClick={() => onSelect?.(variant.id)}
                      >
                        👁️
                      </button>
                      {!servicesOnly ? (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                          onClick={() => navigate(`/inventory/variants/${variant.id}/related-products`)}
                        >
                          Related
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                          onClick={() => navigate(`/inventory/service-variants/${variant.id}/related-products`)}
                        >
                          Related
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            {listLabel}
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} (20 / page)</span>
        }
      />
    </div>
  );
};

export default VariantsPanel;
